import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Activity, Check } from 'lucide-react';
import { plansAPI } from '../api/client';
import { ThemeToggle } from '../components/layout/ThemeToggle';
import { annualSaving, comparisonRows, euros, planFeatures } from '../lib/plans';

/**
 * The public pricing page. Reads the same catalogue the application enforces,
 * so what is advertised here and what a team actually gets cannot drift apart.
 */
export function Pricing() {
  const [annual, setAnnual] = useState(true);
  const { data, isLoading, isError } = useQuery({ queryKey: ['plans'], queryFn: plansAPI.list });

  const plans = data?.plans ?? [];
  const bestSaving = plans.reduce((best, p) => Math.max(best, annualSaving(p)), 0);

  return (
    <div className="pricing-page">
      <header className="pricing-nav">
        <Link to="/" className="pricing-brand">
          <span className="pricing-brand-mark">
            <Activity size={20} aria-hidden="true" />
          </span>
          <span className="pricing-brand-name">Uptimer</span>
        </Link>
        <div className="pricing-nav-actions">
          <ThemeToggle />
          <Link to="/login" className="btn btn-primary">
            Sign in
          </Link>
        </div>
      </header>

      <section className="pricing-hero">
        <h1 className="pricing-title">
          Monitoring that scales with you<span className="pricing-title-dot">.</span>
        </h1>
        <p className="pricing-lede">
          Every plan checks HTTP, ping, port, keyword and heartbeat endpoints, alerts your team the
          moment something breaks, and publishes a status page your customers can trust.
        </p>

        <div className="pricing-billing">
          {bestSaving > 0 && (
            <span className="pricing-saving">Save up to {bestSaving}% with annual billing</span>
          )}
          <div className="billing-toggle" role="group" aria-label="Billing period">
            <button
              type="button"
              className={`billing-toggle-option${annual ? ' is-selected' : ''}`}
              onClick={() => setAnnual(true)}
            >
              Annual
            </button>
            <button
              type="button"
              className={`billing-toggle-option${annual ? '' : ' is-selected'}`}
              onClick={() => setAnnual(false)}
            >
              Monthly
            </button>
          </div>
        </div>
      </section>

      {isLoading && <div className="loading-center" />}

      {isError && (
        <p className="plans-error" role="alert">
          Pricing is temporarily unavailable. Please try again shortly.
        </p>
      )}

      <div className="plan-grid pricing-grid">
        {plans.map(plan => {
          const price = annual ? plan.price_annual_monthly_cents : plan.price_monthly_cents;

          return (
            <div key={plan.id} className={`plan-card${plan.highlighted ? ' is-highlighted' : ''}`}>
              {plan.highlighted && <span className="plan-badge">Most popular</span>}

              <h2 className="plan-name">{plan.name}</h2>

              <div className="plan-price">
                {plan.custom_pricing ? (
                  <span className="plan-price-custom">Let’s talk</span>
                ) : (
                  <>
                    {annual && plan.price_monthly_cents > plan.price_annual_monthly_cents && (
                      <span className="plan-price-was">{euros(plan.price_monthly_cents)}</span>
                    )}
                    <span className="plan-price-now">{euros(price)}</span>
                    <span className="plan-price-period">/month</span>
                  </>
                )}
              </div>

              <p className="plan-description">{plan.description}</p>

              {plan.custom_pricing ? (
                <a className="btn btn-secondary plan-cta" href="mailto:sales@uptimer.tsew.com">
                  Book a demo
                </a>
              ) : (
                <Link
                  to="/login"
                  className={`btn ${plan.highlighted ? 'btn-primary' : 'btn-secondary'} plan-cta`}
                >
                  {plan.price_monthly_cents === 0 ? 'Start free' : `Get ${plan.name}`}
                </Link>
              )}

              <ul className="plan-features">
                {planFeatures(plan).map(feature => (
                  <li key={feature}>
                    <Check size={14} aria-hidden="true" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {plans.length > 0 && (
        <section className="pricing-compare">
          <h2 className="pricing-section-title">Compare plans</h2>
          <div className="pricing-table-scroll">
            <table className="table pricing-table">
              <thead>
                <tr>
                  <th scope="col">Feature</th>
                  {plans.map(p => (
                    <th key={p.id} scope="col">
                      {p.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map(row => (
                  <tr key={row.label}>
                    <th scope="row">{row.label}</th>
                    {plans.map(p => (
                      <td key={p.id}>{row.value(p)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="pricing-faq">
        <h2 className="pricing-section-title">Questions</h2>
        <dl>
          <dt>Do I need a card to start?</dt>
          <dd>No. The Free plan needs no payment details and does not expire.</dd>

          <dt>Can I change plan later?</dt>
          <dd>
            Yes, at any time from your account. Downgrades apply once you are within the smaller
            plan’s limits, so nothing is ever deleted on your behalf.
          </dd>

          <dt>What happens if I go over a limit?</dt>
          <dd>
            Existing monitors keep running. You will be asked to upgrade before adding more.
          </dd>

          <dt>What counts as a monitor?</dt>
          <dd>One endpoint checked on a schedule — a URL, host, port or heartbeat.</dd>
        </dl>
      </section>

      <section className="pricing-cta">
        <h2 className="pricing-section-title">Start monitoring in a couple of minutes</h2>
        <p>No card, no trial countdown.</p>
        <Link to="/login" className="btn btn-primary">
          Create your account
        </Link>
      </section>
    </div>
  );
}
